import { Injectable } from '@angular/core';
import { ApiService } from '@/pages/service/core/api.service';

/**
 * Talks to the SEO add-on through workflow-definer's /api/seo proxy. Per-company:
 * the proxy resolves the company from the logged-in user's JWT, so the screens
 * always operate on that company's own SEO.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {

    private readonly base = '/api/seo/v1';

    constructor(private api: ApiService) {}

    // ── Dashboard ──
    summary()        { return this.api.get<any>(`${this.base}/summary`); }

    // ── Posts ──
    posts()          { return this.api.get<any[]>(`${this.base}/posts`); }
    generatePost()   { return this.api.post<any>(`${this.base}/posts/generate`, {}); }
    approvePost(id: number) { return this.api.post<any>(`${this.base}/posts/${id}/approve`, {}); }
    rejectPost(id: number)  { return this.api.post<any>(`${this.base}/posts/${id}/reject`, {}); }
    postHtml(id: number)    { return this.api.get<any>(`${this.base}/posts/${id}/html`); }

    // ── Keywords ──
    keywords()             { return this.api.get<any[]>(`${this.base}/keywords`); }
    addKeyword(phrase: string) { return this.api.post<any>(`${this.base}/keywords`, { phrase }); }
    deleteKeyword(id: number)  { return this.api.delete<any>(`${this.base}/keywords/${id}`); }
    discoverKeywords()         { return this.api.post<any>(`${this.base}/keywords/discover`, {}); }
    writeKeyword(id: number)   { return this.api.post<any>(`${this.base}/keywords/${id}/write`, {}); }

    // ── Audit ──
    latestAudit()   { return this.api.get<any>(`${this.base}/audit/latest`); }
    runAudit()      { return this.api.post<any>(`${this.base}/audit/run`, {}); }

    // ── Suggestions (the prioritised "fix these first" list) ──
    suggestions()                 { return this.api.get<any[]>(`${this.base}/suggestions`); }
    suggestionDone(id: number)    { return this.api.post<any>(`${this.base}/suggestions/${id}/done`, {}); }
    suggestionDismiss(id: number) { return this.api.post<any>(`${this.base}/suggestions/${id}/dismiss`, {}); }

    // ── Report + Strategy ──
    latestReport()   { return this.api.get<any>(`${this.base}/report/latest`); }
    latestStrategy() { return this.api.get<any>(`${this.base}/strategy/latest`); }

    // ── Settings / publish mode ──
    getConfig()           { return this.api.get<any>(`${this.base}/config`); }
    saveConfig(cfg: any)  { return this.api.put<any>(`${this.base}/config`, cfg); }

    // ── Add-on status + onboarding ──
    status()              { return this.api.get<any>(`${this.base}/status`); }

    // ── Local Maps (geo-grid heat-map) ──
    geoLocate(query: string) { return this.api.post<any>(`${this.base}/geo/locate`, { query }); }
    geoScan(keyword: string) { return this.api.post<any>(`${this.base}/geo/scan`, { keyword }); }
    geoLatest(keyword?: string) {
        return this.api.get<any>(`${this.base}/geo/latest${keyword ? '?keyword=' + encodeURIComponent(keyword) : ''}`);
    }

    // ── AI Visibility (GEO) ──
    aivEngines()    { return this.api.get<any[]>(`${this.base}/geo/engines`); }
    aivPrompts()    { return this.api.get<any[]>(`${this.base}/geo/prompts`); }
    aivQuestions()  { return this.api.get<any[]>(`${this.base}/geo/questions`); }
    aivGenerate()   { return this.api.post<any>(`${this.base}/geo/prompts/generate`, {}); }
    aivRun()        { return this.api.post<any>(`${this.base}/geo/run-now`, {}); }
    aivVisibility() { return this.api.get<any>(`${this.base}/geo/visibility`); }
}
